import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Space, Tag, Typography, Input, Select,
  Popconfirm, message, Row, Col, Card, Statistic
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, QrcodeOutlined
} from '@ant-design/icons';
import { sheepApi } from '../api';
import { Sheep } from '../types';
import SheepForm from '../components/SheepForm';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const statusColors: Record<string, string> = {
  alive: 'green', for_sale: 'orange', sold: 'blue', deceased: 'red',
};
const statusLabels: Record<string, string> = {
  alive: 'Живой', for_sale: 'В продаже', sold: 'Продан', deceased: 'Погиб',
};

interface Props { mine?: boolean }

export default function SheepListPage({ mine = false }: Props) {
  const [sheep, setSheep] = useState<Sheep[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSheep, setEditingSheep] = useState<Sheep | undefined>();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [qrModal, setQrModal] = useState<{ open: boolean; qr: string; name: string }>({
    open: false, qr: '', name: '',
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = (s: Sheep) => isAdmin || s.ownerId === user?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await sheepApi.list(mine);
      setSheep(r.data);
    } finally {
      setLoading(false);
    }
  }, [mine]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    await sheepApi.remove(id);
    message.success('Удалено');
    load();
  };

  const handleQR = async (s: Sheep) => {
    const r = await sheepApi.qr(s.id);
    setQrModal({ open: true, qr: r.data.qr, name: s.name });
  };

  const handleSuccess = () => {
    setModalOpen(false);
    setEditingSheep(undefined);
    load();
    message.success('Сохранено');
  };

  const filtered = sheep.filter((s) => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.name.toLowerCase().includes(q) || s.tagNumber.toLowerCase().includes(q);
    const matchStatus = !statusFilter || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const alive    = sheep.filter((s) => s.status === 'alive').length;
  const forSale  = sheep.filter((s) => s.status === 'for_sale').length;
  const sold     = sheep.filter((s) => s.status === 'sold').length;
  const deceased = sheep.filter((s) => s.status === 'deceased').length;

  // ── Десктопные колонки таблицы ──
  const columns = [
    {
      title: 'Тег', dataIndex: 'tagNumber', key: 'tagNumber', width: 100,
      render: (t: string) => <Tag color="purple">{t}</Tag>,
    },
    { title: 'Кличка', dataIndex: 'name', key: 'name', render: (n: string) => <b>{n}</b> },
    {
      title: 'Пол', dataIndex: 'gender', key: 'gender', width: 90,
      render: (g: string) => g === 'male' ? '♂ Самец' : '♀ Самка',
    },
    {
      title: 'Дата рождения', dataIndex: 'birthDate', key: 'birthDate', width: 130,
      render: (d: string) => new Date(d).toLocaleDateString('ru-RU'),
    },
    { title: 'Порода', dataIndex: 'breed', key: 'breed', render: (b: string) => b || '—' },
    {
      title: 'Вес', dataIndex: 'weight', key: 'weight', width: 80,
      render: (w: number) => w ? `${w} кг` : '—',
    },
    {
      title: 'Статус', dataIndex: 'status', key: 'status', width: 110,
      render: (s: string) => <Tag color={statusColors[s]}>{statusLabels[s]}</Tag>,
    },
    {
      title: '', key: 'actions', width: 140,
      render: (_: any, record: Sheep) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />}
            onClick={() => navigate(`/sheep/${record.id}/detail`)} />
          <Button size="small" icon={<QrcodeOutlined />} onClick={() => handleQR(record)} />
          {canEdit(record) && <>
            <Button size="small" icon={<EditOutlined />} onClick={() => {
              setEditingSheep(record); setModalOpen(true);
            }} />
            <Popconfirm title="Удалить барана?" onConfirm={() => handleDelete(record.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </>}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* ── Статистика ── */}
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
        {[
          { title: 'Всего',       value: sheep.length,    color: undefined },
          { title: 'Живых',       value: alive,            color: '#3f8600' },
          { title: 'В продаже',   value: forSale,          color: '#d46b08' },
          { title: 'Прод/Погибло',value: sold + deceased,  color: '#cf1322' },
        ].map((s) => (
          <Col key={s.title} xs={12} sm={6}>
            <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
              <Statistic title={s.title} value={s.value}
                valueStyle={{ color: s.color, fontSize: 22 }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Фильтры + кнопка ── */}
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }} align="middle">
        <Col xs={14} sm={10} md={8}>
          <Input.Search placeholder="Кличка или тег" value={search}
            onChange={(e) => setSearch(e.target.value)} allowClear />
        </Col>
        <Col xs={10} sm={7} md={6}>
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }} placeholder="Все">
            <Select.Option value="">Все</Select.Option>
            <Select.Option value="alive">Живые</Select.Option>
            <Select.Option value="for_sale">В продаже</Select.Option>
            <Select.Option value="sold">Проданные</Select.Option>
            <Select.Option value="deceased">Погибшие</Select.Option>
          </Select>
        </Col>
        {(isAdmin || mine) && (
          <Col xs={24} sm={7} md={10} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" icon={<PlusOutlined />} block
              onClick={() => { setEditingSheep(undefined); setModalOpen(true); }}>
              Добавить барана
            </Button>
          </Col>
        )}
      </Row>

      {/* ── Мобильные карточки ── */}
      <div className="mobile-cards">
        {loading
          ? <div style={{ textAlign: 'center', padding: 24 }}>Загрузка...</div>
          : filtered.length === 0
            ? <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>Ничего не найдено</div>
            : filtered.map((s) => (
              <Card
                key={s.id}
                size="small"
                style={{ marginBottom: 8, borderRadius: 10 }}
                styles={{ body: { padding: '10px 12px' } }}
                onClick={() => navigate(`/sheep/${s.id}/detail`)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <b style={{ fontSize: 15 }}>{s.gender === 'male' ? '♂' : '♀'} {s.name}</b>
                      <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>{s.tagNumber}</Tag>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Tag color={statusColors[s.status]} style={{ margin: 0, fontSize: 11 }}>
                        {statusLabels[s.status]}
                      </Tag>
                      {s.breed && <span style={{ fontSize: 12, color: '#666' }}>{s.breed}</span>}
                      {s.weight && <span style={{ fontSize: 12, color: '#666' }}>{s.weight} кг</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 8 }} onClick={(e) => e.stopPropagation()}>
                    <Button size="small" icon={<QrcodeOutlined />} onClick={() => handleQR(s)} />
                    {canEdit(s) && <>
                      <Button size="small" icon={<EditOutlined />} onClick={() => {
                        setEditingSheep(s); setModalOpen(true);
                      }} />
                      <Popconfirm title="Удалить?" onConfirm={() => handleDelete(s.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </>}
                  </div>
                </div>
              </Card>
            ))
        }
      </div>

      {/* ── Десктопная таблица ── */}
      <div className="desktop-table">
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 700 }}
          pagination={{ pageSize: 20, size: 'small' }}
          size="small"
        />
      </div>

      {/* ── Модалки ── */}
      <Modal
        title={editingSheep ? 'Редактировать барана' : 'Добавить барана'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingSheep(undefined); }}
        footer={null}
        width="min(700px, 96vw)"
        style={{ top: 8 }}
        destroyOnClose
      >
        <SheepForm
          initial={editingSheep}
          onSuccess={handleSuccess}
          onCancel={() => { setModalOpen(false); setEditingSheep(undefined); }}
        />
      </Modal>

      <Modal
        title={`QR-код: ${qrModal.name}`}
        open={qrModal.open}
        onCancel={() => setQrModal({ open: false, qr: '', name: '' })}
        width="min(360px, 92vw)"
        footer={[
          <Button key="print" type="primary" onClick={() => {
            const w = window.open('');
            w?.document.write(`<img src="${qrModal.qr}" />`);
            w?.print();
          }}>
            Распечатать
          </Button>,
        ]}
      >
        <div style={{ textAlign: 'center' }}>
          <img src={qrModal.qr} alt="QR" style={{ maxWidth: '100%', width: 260 }} />
          <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
            Отсканируйте QR-код для просмотра информации
          </Typography.Paragraph>
        </div>
      </Modal>

      <style>{`
        @media (max-width: 767px) {
          .mobile-cards { display: block !important; }
          .desktop-table { display: none !important; }
        }
        @media (min-width: 768px) {
          .mobile-cards { display: none !important; }
          .desktop-table { display: block !important; }
        }
      `}</style>
    </div>
  );
}
